import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Calendar, MapPin, Share2, Copy, Check, MessageCircle, Mail,
  Trash2, Pencil, Gift, Sparkles, ArrowUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

import AddressModal from '@/components/AddressModal';
import { PageShell } from '@/components/brand/PageShell';
import { LoadingState, EmptyState, ErrorState } from '@/components/brand/States';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';
import { formatPrice } from '@/lib/formatPrice';
import { capture } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

// Items can carry their own currency_code (default INR for legacy items)
const itemPrice = (item, value = null) =>
  formatPrice(value === null ? item.price : value, item.currency_code || 'INR');

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

const eventTypeLabel = (type) => {
  const map = { Baby: 'Baby registry', Wedding: 'Wedding registry', Birthday: 'Birthday registry', Custom: 'Custom registry' };
  return map[type] || `${type} registry`;
};

const statusBadge = {
  available:        { label: 'Available',        cls: 'bg-sage-100 text-sage-700 border-sage-200' },
  reserved:         { label: 'Reserved',         cls: 'bg-cream-200 text-clay-700 border-cream-300' },
  purchased:        { label: 'Gifted',           cls: 'bg-clay-100 text-clay-700 border-clay-200' },
  fully_funded:     { label: 'Fully gifted',     cls: 'bg-clay-100 text-clay-700 border-clay-200' },
  partially_funded: { label: 'Partly gifted',    cls: 'bg-cream-200 text-clay-700 border-cream-300' },
};

/* -------------------------------------------------------------------------- */
/*  Item card                                                                  */
/* -------------------------------------------------------------------------- */

const ItemCard = ({ item, isOwner, onBuy, onMarkGifted, onMarkAvailable, onEdit, onDelete }) => {
  const status = statusBadge[item.status] || statusBadge.available;
  const isPurchased = item.status === 'purchased' || item.status === 'fully_funded';
  const isReserved = item.status === 'reserved';
  const hasContribution = item.total_contributed > 0;
  const progress = hasContribution && item.price ? Math.min(100, Math.round((item.total_contributed / item.price) * 100)) : 0;

  return (
    <article className="group relative flex flex-col rounded-2xl bg-cream-50 border border-border/70 overflow-hidden shadow-soft-1 hover:shadow-soft-2 transition-shadow">
      {/* Image */}
      <div className="relative aspect-square bg-cream-100 overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-cream-300">
            <Gift className="h-10 w-10" />
          </div>
        )}
        <span className={`absolute top-3 left-3 inline-block text-[11px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border backdrop-blur-sm ${status.cls}`}>
          {status.label}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 p-5 flex flex-col">
        <h3 className="font-display text-lg text-ink leading-snug line-clamp-2">{item.name}</h3>
        {item.description && (
          <p className="mt-1.5 text-sm text-ink-muted line-clamp-2">{item.description}</p>
        )}
        {item.price ? (
          <p className="mt-3 font-display text-xl text-ink">{itemPrice(item)}</p>
        ) : null}

        {/* Group-gift progress */}
        {hasContribution && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-[12px] text-ink-muted">{itemPrice(item, item.total_contributed)} of {itemPrice(item)}</span>
              <span className="text-[12px] font-medium text-clay-700">{progress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-cream-200 overflow-hidden">
              <div className="h-full bg-clay-500 rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {isPurchased && item.purchased_by && (
          <p className="mt-3 text-[13px] text-ink-soft italic">
            Gifted by <span className="not-italic font-medium text-ink">{item.purchased_by}</span>
          </p>
        )}

        {/* Actions — owner vs guest, by status */}
        <div className="mt-auto pt-5 flex flex-col gap-2">
          {isOwner ? (
            <>
              {isPurchased ? (
                <BrandButton variant="outline" size="sm" onClick={() => onMarkAvailable(item)}>
                  Mark as available
                </BrandButton>
              ) : isReserved ? (
                <div className="text-xs text-ink-muted text-center py-2">
                  Reserved — waiting on confirmation
                </div>
              ) : (
                <div className="text-xs text-ink-muted text-center py-2">
                  Available for your guests
                </div>
              )}
              <div className="flex gap-2">
                <BrandButton variant="ghost" size="sm" onClick={() => onEdit(item)} className="flex-1">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </BrandButton>
                <BrandButton variant="ghost" size="sm" onClick={() => onDelete(item)} className="flex-1 hover:!bg-clay-50 hover:!text-clay-700">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </BrandButton>
              </div>
            </>
          ) : (
            <>
              {isPurchased ? (
                <BrandButton variant="outline" size="sm" disabled>
                  Already gifted
                </BrandButton>
              ) : (
                <>
                  <BrandButton variant="primary" size="sm" onClick={() => onBuy(item)}>
                    <ArrowUpRight className="h-3.5 w-3.5" /> Buy this gift
                  </BrandButton>
                  <BrandButton variant="ghost" size="sm" onClick={() => onMarkGifted(item)}>
                    Already bought elsewhere
                  </BrandButton>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </article>
  );
};

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const RegistryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showReserve, setShowReserve] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [buyerName, setBuyerName] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showMarkGifted, setShowMarkGifted] = useState(false);
  const [giftedByName, setGiftedByName] = useState('');
  const [giftedByEmail, setGiftedByEmail] = useState('');
  const [showThankYou, setShowThankYou] = useState(false);
  const [thankYouData, setThankYouData] = useState(null);

  const isOwner = user && registry && user.id === registry.user_id;

  /* ---------- effects ---------- */
  useEffect(() => { fetchRegistry(); }, [id]); // eslint-disable-line

  const fetchRegistry = async () => {
    try {
      const response = await axios.get(`${API}/registry/${id}`);
      setRegistry(response.data);
    } catch (error) {
      toast.error("Couldn't load this registry");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- share ---------- */
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied');
    capture('registry_shared', { channel: 'copy' });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsAppShare = () => {
    const userName = registry.user_name || 'Our';
    const eventType = registry.event_type;
    const message = `${userName} ${eventType} registry on Joy Basket\n\nView & gift → ${window.location.href}`;
    capture('registry_shared', { channel: 'whatsapp' });
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleEmailShare = () => {
    const eventType = registry.event_type;
    const subject = `Our ${eventType} registry on Joy Basket`;
    const body = `I'd love for you to see our Joy Basket registry.\n\n${window.location.href}\n\nWith love.`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  const getSocialCaption = () => {
    const userName = registry.user_name || 'Our';
    return `${userName} ${registry.event_type} registry on Joy Basket\n\nView & gift → ${window.location.href}`;
  };

  const handleCopySocialCaption = () => {
    navigator.clipboard.writeText(getSocialCaption());
    toast.success('Caption copied');
  };

  /* ---------- old reserve flow (kept for compatibility) ---------- */
  const handleReserveClick = (item) => {
    setSelectedItem(item);
    setShowReserve(true);
  };

  const handleConfirmReservation = async () => {
    if (!buyerName.trim()) return toast.error('Please enter your name');
    try {
      await axios.post(`${API}/registry/${id}/items/${selectedItem.id}/reserve`, {
        reserved_by_name: buyerName,
        reserved_by_email: buyerEmail,
      });
      capture('gift_reserved');
      setShowReserve(false);
      setShowPurchase(true);
      await fetchRegistry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reservation failed');
    }
  };

  const handleProceedToPurchase = async () => {
    if (!selectedItem) return toast.error('No item selected');

    let productUrl = selectedItem.affiliate_link || selectedItem.url;
    if (!productUrl || productUrl === '#' || productUrl === 'null') {
      const searchQuery = selectedItem.name.replace(/\s+/g, '+');
      productUrl = `https://www.amazon.in/s?k=${searchQuery}&tag=joybasket-21`;
    }

    try {
      await axios.post(`${API}/affiliate/track-click`, {
        registry_id: id,
        item_id: selectedItem.id,
        product_url: productUrl,
      });
    } catch (e) { /* tracking is non-blocking */ }

    toast.success('Taking you to the partner store…', { duration: 2000 });

    setTimeout(() => {
      const opened = window.open(productUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        toast.error('Please allow popups, or click here to continue', {
          action: { label: 'Open link', onClick: () => (window.location.href = productUrl) },
        });
      } else {
        setShowPurchase(false);
        toast.info("After your purchase, return here and 'Already bought elsewhere' to mark the gift.", { duration: 8000 });
      }
    }, 500);
  };

  const handleConfirmPurchase = async () => {
    try {
      await axios.post(`${API}/registry/${id}/items/${selectedItem.id}/purchase`, {
        purchased_by_name: buyerName,
        purchased_by_email: buyerEmail,
      });
      capture('gift_purchase_confirmed', { flow: 'reserve_then_buy' });
      toast.success('Gift confirmed. Thank you.');
      setShowPurchase(false);
      setBuyerName(''); setBuyerEmail(''); setSelectedItem(null);
      await fetchRegistry();
    } catch (error) {
      toast.error('Could not confirm purchase');
    }
  };

  /* ---------- new buy-direct + mark-gifted flow ---------- */
  const handleBuyOnAmazon = async (item) => {
    if (item.status === 'purchased') return toast.error('This item has already been gifted.');
    await fetchRegistry();
    setSelectedItem(item);
    setShowAddressModal(true);
  };

  const handleProceedToAmazon = () => {
    if (!selectedItem) return;
    let productUrl = selectedItem.affiliate_link || selectedItem.url;
    if (!productUrl || productUrl === '#' || productUrl === 'null') {
      const searchQuery = selectedItem.name.replace(/\s+/g, '+');
      productUrl = `https://www.amazon.in/s?k=${searchQuery}&tag=joybasket-21`;
    }
    window.open(productUrl, '_blank');
    setShowAddressModal(false);
    toast.info("After your purchase, come back to mark it as gifted.", { duration: 6000 });
  };

  const handleMarkAsGifted = (item) => {
    setSelectedItem(item);
    setGiftedByName(''); setGiftedByEmail('');
    setShowMarkGifted(true);
  };

  const handleConfirmGifted = async () => {
    if (!giftedByName.trim()) return toast.error('Please enter your name');
    try {
      await axios.post(`${API}/registry/${id}/items/${selectedItem.id}/purchase`, {
        purchased_by_name: giftedByName,
        purchased_by_email: giftedByEmail,
      });
      capture('gift_purchase_confirmed', { flow: 'mark_gifted' });
      setThankYouData({
        buyerName: giftedByName,
        itemName: selectedItem.name,
        registryOwner: registry.user_name,
      });
      setShowMarkGifted(false);
      setShowThankYou(true);
      await fetchRegistry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as gifted');
    }
  };

  /* ---------- owner-only ---------- */
  const handleDeleteItem = async (item) => {
    if (!isOwner) return toast.error('Only the registry owner can remove items');
    if (!window.confirm(`Remove "${item.name}" from your registry?`)) return;
    try {
      await axios.delete(`${API}/registry/${id}/items/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Item removed');
      await fetchRegistry();
    } catch (error) {
      toast.error('Could not remove item');
    }
  };

  const handleMarkAsAvailable = async (item) => {
    if (!window.confirm(`Mark "${item.name}" as available again?`)) return;
    try {
      await axios.post(`${API}/registry/${id}/items/${item.id}/unmark-purchase`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Item is available again');
      await fetchRegistry();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update');
    }
  };

  const handleEditItem = () => navigate(`/registry/${id}/edit`);

  /* ---------- render ---------- */
  if (loading) {
    return <PageShell><LoadingState label="Opening the basket…" /></PageShell>;
  }

  if (!registry) {
    return (
      <PageShell>
        <div className="container-page py-20">
          <ErrorState
            title="This basket isn't here."
            body="The link may have been mistyped, or the registry could be private. Try the search to find a registry."
            action={
              <BrandButton onClick={() => navigate('/search-registry')}>Find a registry</BrandButton>
            }
          />
        </div>
      </PageShell>
    );
  }

  const items = registry.items || [];
  const totalItems = items.length;
  const giftedCount = items.filter((i) => i.status === 'purchased' || i.status === 'fully_funded').length;

  return (
    <PageShell>
      {/* Owner banner */}
      {isOwner && (
        <div className="bg-clay-700 text-cream-50">
          <div className="container-page py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-2.5">
              <Sparkles className="h-4 w-4 text-cream-100" />
              <span>This is your registry, as your guests see it.</span>
            </div>
            <BrandButton
              variant="cream"
              size="sm"
              onClick={() => navigate(`/registry/${id}/edit`)}
              data-testid="manage-registry-btn"
            >
              Manage registry
            </BrandButton>
          </div>
        </div>
      )}

      {/* Hero / header */}
      <section className="border-b border-border/70 bg-cream-100/50">
        <div className="container-page py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-4">{eventTypeLabel(registry.event_type)}</div>
              <h1 className="font-display text-display-xl text-ink leading-[1.05]">
                {registry.user_name || 'Their'}
                <span className="italic text-clay-700" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
                  {' '}basket.
                </span>
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-ink-soft">
                {registry.event_date && (
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <Calendar className="h-4 w-4" />
                    {formatDate(registry.event_date)}
                  </span>
                )}
                {registry.event_details?.city && (
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <MapPin className="h-4 w-4" />
                    {registry.event_details.city}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm">
                  <Gift className="h-4 w-4" />
                  {giftedCount} of {totalItems} gifted
                </span>
              </div>
            </div>

            <div className="lg:col-span-4 flex lg:justify-end">
              <BrandButton size="lg" onClick={() => setShowShare(true)} data-testid="share-btn">
                <Share2 className="h-4 w-4" />
                Share registry
              </BrandButton>
            </div>
          </div>
        </div>
      </section>

      {/* Items */}
      <section className="container-page py-14 sm:py-20">
        {totalItems === 0 ? (
          <EmptyState
            eyebrow="An empty basket"
            title="Nothing here just yet."
            body={
              isOwner
                ? "Add a few things you'd love. Curated picks or a link from any shop you trust."
                : "The basket isn't filled yet. Check back soon — or write a note and ask what's coming."
            }
            action={
              isOwner ? (
                <BrandButton onClick={() => navigate(`/registry/${id}/edit`)}>
                  Add gifts
                </BrandButton>
              ) : null
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
            {items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                isOwner={isOwner}
                onBuy={handleBuyOnAmazon}
                onMarkGifted={handleMarkAsGifted}
                onMarkAvailable={handleMarkAsAvailable}
                onEdit={handleEditItem}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        )}
      </section>

      {/* ---------- Share dialog ---------- */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="sm:max-w-[460px] bg-cream-50 border-border/70 rounded-3xl p-0 overflow-hidden">
          <div className="px-8 pt-8 pb-2 text-center">
            <LogoMark size={26} className="text-clay-700 mx-auto mb-4" />
            <DialogHeader>
              <DialogTitle className="font-display text-[26px] leading-tight text-ink">
                Share your basket.
              </DialogTitle>
            </DialogHeader>
            <p className="mt-2 text-sm text-ink-soft">A single, gentle link.</p>
          </div>
          <div className="px-8 pb-8 pt-4 space-y-5">
            <div className="rounded-2xl bg-cream-100 border border-border p-5 flex items-center justify-center">
              <QRCodeSVG value={window.location.href} size={148} fgColor="#7E3923" bgColor="transparent" />
            </div>

            <button
              onClick={handleCopyLink}
              className="w-full flex items-center justify-between gap-3 rounded-xl bg-cream-100 hover:bg-cream-200 border border-border px-4 py-3 transition-colors"
            >
              <span className="text-sm text-ink-soft truncate">{window.location.href}</span>
              <span className="inline-flex items-center gap-1.5 text-clay-700 text-sm font-medium shrink-0">
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
              </span>
            </button>

            <div className="grid grid-cols-2 gap-3">
              <BrandButton variant="outline" onClick={handleWhatsAppShare}>
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </BrandButton>
              <BrandButton variant="outline" onClick={handleEmailShare}>
                <Mail className="h-4 w-4" /> Email
              </BrandButton>
            </div>

            <button
              onClick={handleCopySocialCaption}
              className="w-full text-center text-sm text-ink-muted hover:text-clay-700 underline decoration-clay-300 underline-offset-4 transition-colors"
            >
              Copy a caption for social
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- Reserve dialog (legacy flow) ---------- */}
      <Dialog open={showReserve} onOpenChange={setShowReserve}>
        <DialogContent className="sm:max-w-md bg-cream-50 border-border/70 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink">Reserve {selectedItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input placeholder="Your name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
            <Input placeholder="Email (optional)" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} />
            <BrandButton className="w-full" onClick={handleConfirmReservation}>Reserve</BrandButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- Purchase redirect dialog ---------- */}
      <Dialog open={showPurchase} onOpenChange={setShowPurchase}>
        <DialogContent className="sm:max-w-md bg-cream-50 border-border/70 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink">Almost there.</DialogTitle>
          </DialogHeader>
          <p className="text-ink-soft text-sm">We'll take you to the partner store. Come back to confirm once your gift's on its way.</p>
          <div className="flex gap-3 pt-2">
            <BrandButton variant="outline" className="flex-1" onClick={() => setShowPurchase(false)}>Cancel</BrandButton>
            <BrandButton className="flex-1" onClick={handleProceedToPurchase}>
              <ArrowUpRight className="h-4 w-4" /> Continue
            </BrandButton>
          </div>
          <button
            onClick={handleConfirmPurchase}
            className="mt-2 text-sm text-ink-muted hover:text-clay-700 underline decoration-clay-300 underline-offset-4"
          >
            Already bought it elsewhere? Mark as gifted.
          </button>
        </DialogContent>
      </Dialog>

      {/* ---------- Address modal (custom component) ---------- */}
      {showAddressModal && (
        <AddressModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          onProceed={handleProceedToAmazon}
          registry={registry}
        />
      )}

      {/* ---------- Mark gifted dialog ---------- */}
      <Dialog open={showMarkGifted} onOpenChange={setShowMarkGifted}>
        <DialogContent className="sm:max-w-md bg-cream-50 border-border/70 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink">Mark as gifted</DialogTitle>
          </DialogHeader>
          <p className="text-ink-soft text-sm">
            Let {registry?.user_name?.split(' ')[0] || 'them'} know who this gift is from.
          </p>
          <div className="space-y-3 pt-1">
            <Input placeholder="Your name" value={giftedByName} onChange={(e) => setGiftedByName(e.target.value)} />
            <Input placeholder="Email (optional)" value={giftedByEmail} onChange={(e) => setGiftedByEmail(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <BrandButton variant="outline" className="flex-1" onClick={() => setShowMarkGifted(false)}>Cancel</BrandButton>
            <BrandButton className="flex-1" onClick={handleConfirmGifted}>Confirm</BrandButton>
          </div>
        </DialogContent>
      </Dialog>

      {/* ---------- Thank you dialog ---------- */}
      <Dialog open={showThankYou} onOpenChange={setShowThankYou}>
        <DialogContent className="sm:max-w-md bg-cream-50 border-border/70 rounded-3xl text-center px-8 py-9">
          <LogoMark size={32} className="text-clay-700 mx-auto mb-5" />
          <DialogTitle className="font-display text-[28px] leading-tight text-ink">
            Thank you, {thankYouData?.buyerName}.
          </DialogTitle>
          <p className="mt-3 text-ink-soft">
            Your gift of <span className="italic text-ink">{thankYouData?.itemName}</span> is on its way to{' '}
            {thankYouData?.registryOwner || 'them'}. They'll be so glad.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-2 justify-center">
            <BrandButton variant="outline" onClick={() => setShowThankYou(false)}>Keep browsing</BrandButton>
            <BrandButton onClick={() => { setShowThankYou(false); setShowShare(true); }}>Share registry</BrandButton>
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default RegistryView;
